import { useState } from 'react';
import {
  Participant,
  ParticipantFieldCategory,
  ProfessionalSituation,
  useCurrentParticipant,
  useParticipantFieldsCategory,
} from '@app/api';
import { Input, CheckboxBlockField } from '@app/form';
import { Theme } from '@app/theme';
import { useField, useFormikContext } from 'formik';
import { createUseStyles } from 'react-jss';
import { InformationSection } from '../../Profile/components/InformationSection';
import { LinkedinConnection } from '../../Profile/components/LinkedinConnection';
import { ParticipantInputByName } from '../../Profile/components/ParticipantInputByName';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  descriptionTitle: {
    ...theme.fonts.label,
    paddingBottom: theme.marginBase / 2,
  },
  description: {
    ...theme.fonts.caption,
    paddingBottom: theme.marginBase / 2,
    color: theme.colors.inkTertiary,
  },
  checkBoxContainer: {},
  checkBoxWrapper: {
    width: '100%',
    display: 'flex',
    gap: theme.marginBase * 2,
  },
}));

interface Props {
  noLinkedinConnection?: boolean;
  noTitle?: boolean;
}

export const Professional = ({ noTitle, noLinkedinConnection }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const categoryConfig = useParticipantFieldsCategory(
    ParticipantFieldCategory.PROFESSIONAL,
  );
  const { data: participant } = useCurrentParticipant(true);

  const [jobTitleCache, setJobTitleCache] = useState<string | null>(
    participant?.jobTitle || null,
  );

  const { values } = useFormikContext<Participant>();
  const [{ value: jobTitle }, _, { setValue: setJobTitle }] =
    useField('jobTitle');

  const isWorkingStudent =
    ProfessionalSituation.STUDENT === values.professionalSituation &&
    jobTitle !== null;

  const isWorkerOrStudent =
    values.professionalSituation === ProfessionalSituation.WORKING ||
    isWorkingStudent;

  if (!categoryConfig) {
    return null;
  }

  return (
    <InformationSection
      title={noTitle ? undefined : categoryConfig.title}
      icon="ri-briefcase-3-line"
      iconColor="blue"
      description={categoryConfig.description}
    >
      <>
        <ParticipantInputByName fieldName="professionalSituation" />
        {values.professionalSituation === ProfessionalSituation.STUDENT && (
          <div className={classes.checkBoxContainer}>
            <h3 className={classes.descriptionTitle}>
              {t('participantSpace.professionalProfile.descriptionTitle')}
            </h3>
            <p className={classes.description}>
              {t('participantSpace.professionalProfile.description')}
            </p>
            <div className={classes.checkBoxWrapper}>
              <CheckboxBlockField
                type="radio"
                variant="green"
                label={t('application.addToPanel.choices.yes')}
                checked={isWorkingStudent}
                onChange={() => {
                  setJobTitle(jobTitleCache || '');
                }}
              />
              <CheckboxBlockField
                type="radio"
                variant="green"
                label={t('application.addToPanel.choices.no')}
                checked={!isWorkingStudent}
                onChange={() => {
                  setJobTitleCache(jobTitle);
                  setJobTitle(null);
                }}
              />
            </div>
          </div>
        )}
        {isWorkerOrStudent && (
          <>
            <ParticipantInputByName fieldName="jobTitle" />
            <ParticipantInputByName fieldName="companySize" />
            <ParticipantInputByName fieldName="hierarchicalLevel" />
            <ParticipantInputByName fieldName="industries" />
            <ParticipantInputByName fieldName="companyType" />
          </>
        )}
        {participant && !noLinkedinConnection && (
          <LinkedinConnection
            hide={!values.professionalSituation}
            participant={participant}
          />
        )}
        {!!values.professionalSituation && (
          <Input
            name="linkedinProfileUrl"
            label={t(
              'participantSpace.professionalProfile.linkedin.profileUrl',
            )}
          />
        )}
      </>
    </InformationSection>
  );
};
